import {
  NoteColoredIcon,
  NoteIcon,
  VwithcircleColoredIcon,
} from '@carboncopyco/icons';
import {
  ColorHex,
  Loading,
  Separator,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { useNetwork } from 'ahooks';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
// eslint-disable-next-line import/no-cycle
import { DiagnosticCheckTypes } from './DiagnosticCheckScreen';

const networkAlert =
  'No internet connection : please check internet connection\n';

interface DiagnosticSectionProps {
  title: string;
  footerSeparator?: boolean;
  runningParams: ((_?: any) => Promise<string> | string)[];
  shouldRun?: boolean;
  setErrorMessages: Dispatch<SetStateAction<string[]>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
}

export const DiagnosticSection = ({
  title,
  runningParams,
  footerSeparator = false,
  shouldRun = false,
  setErrorMessages,
  loading,
  setLoading,
}: DiagnosticSectionProps) => {
  const networkResult = useNetwork();
  const [isPassed, setIsPassed] = useState<boolean>(false);
  const [isOnlineStatus, setIsOnlineStatus] = useState<boolean>(true);
  const icon = useMemo(() => {
    if (loading)
      return (
        <Loading
          color="text-ds-primary-300"
          size={16}
        />
      );
    if (!isOnlineStatus && title !== DiagnosticCheckTypes.Internet_Connection) {
      return <NoteIcon color={ColorHex.natural} />;
    }
    return isPassed ? <VwithcircleColoredIcon /> : <NoteColoredIcon />;
  }, [loading, isOnlineStatus, isPassed]);

  const exeCheck = async () => {
    // eslint-disable-next-line no-restricted-syntax
    for (const [index, task] of runningParams.entries()) {
      // eslint-disable-next-line no-await-in-loop
      const result = await task();
      const check = !result.length;
      if (!check) {
        setErrorMessages((prev) => [...prev, ...result.split('\n')]);
        setIsPassed(false);
        return;
      }
      if (runningParams.length - 1 === index) {
        setIsPassed(true);
      }
    }
  };

  const checkInternetConnection = () => {
    const { online } = networkResult;
    if (!online) {
      setErrorMessages((prev) => {
        if (prev.includes(networkAlert)) {
          setIsPassed(false);
          return prev;
        }
        return [...prev, networkAlert];
      });
    }
    setIsOnlineStatus(!!online);
    return online;
  };

  const runCheck = async () => {
    try {
      const checkInternet = checkInternetConnection();
      if (!checkInternet) return;
      await exeCheck();
    } catch (e) {
      const error = `Error running diagnostic check: ${title}`;
      setErrorMessages((prev) => [...prev, error]);
      setIsPassed(false);
      console.error(error, e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      runCheck();
    }
  }, [loading]);
  return (
    <div className="flex flex-col h-[78px] justify-center">
      <div className="flex flex-row justify-between h-full align-center">
        <TextLargeParagraph
          label={title}
          color="text-ds-natural-500"
        />
        {shouldRun && icon}
      </div>
      {footerSeparator && (
        <div>
          <Separator
            color="bg-ds-natural-150"
            horizontal
          />
        </div>
      )}
    </div>
  );
};
